/**
 * InfluenceGraphView — SVG timeline grafo delle influenze.
 *
 * Ported from ed-tool-philosophy with these literature-specific adaptations:
 *   - 5 typed relation arrows colored per kind (RELATION_COLORS)
 *   - Filters by period / movement / genre (from FilterPanel)
 *   - No author portrait yet — circle with initials + period accent color
 *   - i18n EN/IT strings (header + hints)
 *
 * Interactions kept identical to philosophy:
 *   - Tap node 1st time → persistent selection; viewBox zooms 1.67×;
 *     incident edges highlighted, non-connected nodes dimmed
 *   - Tap same node 2nd time within 320ms → navigate:author
 *   - Tap background → reset selection + viewBox
 *   - Hover (desktop only, when no selection) → preview highlight
 *   - filter:change from sidebar → filter-driven dimming
 *   - Mouse drag = pan, mouse wheel = zoom (anchor at cursor)
 *   - 1-finger touch = pan, 2-finger touch = pinch-zoom (anchor at midpoint)
 *   - ResizeObserver re-runs layout when container resizes (debounced 200ms)
 */

import { BaseComponent } from '@/core/BaseComponent';
import { getDataService } from '@/services/data-registry';
import type { AuthorFilters } from '@/services/IDataService';
import type { AuthorCatalog } from '@/data/schema';
import { computeInitials } from '@/data/initials';
import { authorSphereColor, sphereGradId, sphereStops, sphereRim } from './author-sphere';
import {
  computeLayout,
  RELATION_COLORS,
  RELATION_ORDER,
  type GraphLayout,
  type GraphNode,
  type RelationKind,
} from './graph-layout';

const SVG_NS = 'http://www.w3.org/2000/svg';
const DOUBLE_TAP_WINDOW_MS = 320;
const ZOOM_FACTOR = 0.1;
const MIN_ZOOM = 0.15;
const MAX_ZOOM = 1.0;

interface ViewBox { x: number; y: number; w: number; h: number; }

/** Stato serializzabile (nodo selezionato) → ripristino al "← Indietro". */
export interface InfluenceState { selectedId: string | null; }

export class InfluenceGraphView extends BaseComponent {
  private allAuthors: AuthorCatalog[] = [];
  private layout: GraphLayout | null = null;
  private svgEl: SVGSVGElement | null = null;
  private filters: AuthorFilters = {};
  private resizeTimer: number | undefined;

  private selectedId: string | null = null;
  private connectedToSelected = new Set<string>();

  private lastTapId: string | null = null;
  private lastTapTime = 0;

  private baseViewBox: ViewBox = { x: 0, y: 0, w: 0, h: 0 };
  private currentViewBox: ViewBox = { x: 0, y: 0, w: 0, h: 0 };

  private isPanning = false;
  private panDidMove = false;
  private panStartScreen = { x: 0, y: 0 };
  private panStartViewBox = { x: 0, y: 0 };
  private resizeObserver: ResizeObserver | null = null;

  private pinchStartDist = 0;
  private pinchStartViewBox: ViewBox = { x: 0, y: 0, w: 0, h: 0 };
  private pendingSelect: string | null = null;

  constructor(
    element: HTMLElement,
    context: ConstructorParameters<typeof BaseComponent>[1],
    initial?: InfluenceState,
  ) {
    super(element, context);
    this.pendingSelect = initial?.selectedId ?? null;
  }

  /** Snapshot dello stato (nodo selezionato) per il "← Indietro". */
  captureState(): InfluenceState {
    return { selectedId: this.selectedId };
  }

  protected async onInit(): Promise<void> {
    this.element.classList.add('influence-graph-view');
    this.render();
    if (this.pendingSelect) this.selectNode(this.pendingSelect);

    this.addCleanup(
      this.context.bus.on<AuthorFilters>('filter:change', (filters) => {
        this.filters = filters;
        this.applyVisibility();
      }),
    );

    const container = this.element.querySelector<HTMLElement>('[data-role="graph-container"]')!;
    this.resizeObserver = new ResizeObserver(() => {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => this.renderGraph(), 200);
    });
    this.resizeObserver.observe(container);
    this.addCleanup(() => {
      this.resizeObserver?.disconnect();
      window.clearTimeout(this.resizeTimer);
    });
  }

  protected onLocaleChange(): void {
    this.render();
  }

  private render(): void {
    this.allAuthors = getDataService().listAuthors();
    this.element.innerHTML = `
      <header class="igv-header">
        <div class="igv-header-text">
          <h1 class="igv-title">${this.t('nav.influenceGraph')}</h1>
          <p class="igv-subtitle">${this.t('graph.hint')}</p>
        </div>
        ${this.renderLegend()}
      </header>
      <div class="graph-container" data-role="graph-container"></div>
    `;

    this.renderGraph();
  }

  /** Tipi di relazione effettivamente presenti tra gli autori del dataset. */
  private presentKinds(): Set<string> {
    const ids = new Set(this.allAuthors.map((a) => a.id));
    const present = new Set<string>();
    for (const a of this.allAuthors) {
      for (const k of RELATION_ORDER) {
        const arr = (a.relations?.[k] ?? []) as string[];
        if (arr.some((tid) => ids.has(tid))) present.add(k);
      }
    }
    return present;
  }

  private renderLegend(): string {
    // Mostra solo le relazioni davvero presenti (con pochi autori la legenda a
    // 5 colori confondeva: ne compariva uno solo nel grafo).
    const present = this.presentKinds();
    const kinds = RELATION_ORDER.filter((k) => present.has(k));
    const shown = kinds.length > 0 ? kinds : RELATION_ORDER;
    return `
      <ul class="igv-legend" aria-label="${this.t('graph.legend.title')}">
        ${shown.map(k => `
          <li class="igv-legend-item">
            <span class="igv-legend-swatch" style="background:${RELATION_COLORS[k]}"></span>
            ${this.t(`relations.${k}`)}
          </li>
        `).join('')}
      </ul>
    `;
  }

  private renderGraph(): void {
    const container = this.element.querySelector<HTMLElement>('[data-role="graph-container"]');
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const width = Math.max(Math.floor(rect.width), 800);
    const height = Math.max(Math.floor(rect.height), 500);

    this.layout = computeLayout(this.allAuthors, {
      width, height,
      padding: { top: 50, right: 40, bottom: 40, left: 40 },
      nodeRadius: 26,
    });

    this.baseViewBox = { x: 0, y: 0, w: width, h: height };

    const svg = document.createElementNS(SVG_NS, 'svg') as SVGSVGElement;
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.classList.add('influence-graph-svg');
    this.svgEl = svg;
    this.applyViewBox(this.baseViewBox);

    // NB: niente marker-freccia. È una timeline: la relazione va sempre
    // passato→futuro (sx→dx) e la direzione è già implicita dalla posizione,
    // quindi le punte risultavano invadenti e ridondanti.
    const defs = document.createElementNS(SVG_NS, 'defs');
    // Sfera 3D (gradiente radiale: luce in alto-sx → bordeaux → scuro) + ombra
    // morbida → profondità sui nodi autore.
    const grad = document.createElementNS(SVG_NS, 'radialGradient');
    grad.setAttribute('id', 'node-sphere');
    grad.setAttribute('cx', '35%');
    grad.setAttribute('cy', '28%');
    grad.setAttribute('r', '78%');
    ([['0%', '#8c2935'], ['55%', '#7a1f2b'], ['100%', '#651826']] as const).forEach(([o, c]) => {
      const s = document.createElementNS(SVG_NS, 'stop');
      s.setAttribute('offset', o);
      s.setAttribute('stop-color', c);
      grad.appendChild(s);
    });
    defs.appendChild(grad);
    const filt = document.createElementNS(SVG_NS, 'filter');
    filt.setAttribute('id', 'node-shadow');
    filt.setAttribute('x', '-50%');
    filt.setAttribute('y', '-50%');
    filt.setAttribute('width', '200%');
    filt.setAttribute('height', '200%');
    const ds = document.createElementNS(SVG_NS, 'feDropShadow');
    ds.setAttribute('dx', '0');
    ds.setAttribute('dy', '3');
    ds.setAttribute('stdDeviation', '3.5');
    ds.setAttribute('flood-color', '#120d0b');
    ds.setAttribute('flood-opacity', '0.38');
    filt.appendChild(ds);
    defs.appendChild(filt);
    // Gradienti sfera per EPOCA (logica colore condivisa con la Mappa dei temi)
    for (const c of [...new Set(this.layout.nodes.map((n) => this.nodeColor(n)))]) {
      const eg = document.createElementNS(SVG_NS, 'radialGradient');
      eg.setAttribute('id', sphereGradId(c));
      eg.setAttribute('cx', '33%'); eg.setAttribute('cy', '25%'); eg.setAttribute('r', '84%');
      for (const s of sphereStops(c)) {
        const stop = document.createElementNS(SVG_NS, 'stop');
        stop.setAttribute('offset', s.offset);
        stop.setAttribute('stop-color', s.color);
        eg.appendChild(stop);
      }
      defs.appendChild(eg);
    }
    svg.appendChild(defs);

    // Axis
    const axisGroup = document.createElementNS(SVG_NS, 'g');
    axisGroup.setAttribute('class', 'graph-axis');
    for (const t of this.layout.axisTicks) {
      const line = document.createElementNS(SVG_NS, 'line');
      line.setAttribute('x1', String(t.x));
      line.setAttribute('x2', String(t.x));
      line.setAttribute('y1', '30');
      line.setAttribute('y2', String(height - 25));
      line.setAttribute('class', 'axis-gridline');
      axisGroup.appendChild(line);
      const label = document.createElementNS(SVG_NS, 'text');
      label.setAttribute('x', String(t.x));
      label.setAttribute('y', String(height - 8));
      label.setAttribute('class', 'axis-label');
      label.setAttribute('text-anchor', 'middle');
      label.textContent = t.label;
      axisGroup.appendChild(label);
    }
    svg.appendChild(axisGroup);

    // Edges (one path per relation kind, with directional marker)
    const edgesGroup = document.createElementNS(SVG_NS, 'g');
    edgesGroup.setAttribute('class', 'graph-edges');
    for (const e of this.layout.edges) {
      const path = document.createElementNS(SVG_NS, 'path');
      const dx = e.tx - e.sx;
      const midX = (e.sx + e.tx) / 2;
      const midY = (e.sy + e.ty) / 2 - Math.abs(dx) * 0.12;
      path.setAttribute('d', `M ${e.sx} ${e.sy} Q ${midX} ${midY} ${e.tx} ${e.ty}`);
      path.setAttribute('class', `graph-edge graph-edge-${e.kind}`);
      path.setAttribute('stroke', RELATION_COLORS[e.kind]);
      path.dataset.source = e.sourceId;
      path.dataset.target = e.targetId;
      path.dataset.kind = e.kind;
      edgesGroup.appendChild(path);
    }
    svg.appendChild(edgesGroup);

    // Nodes
    const nodesGroup = document.createElementNS(SVG_NS, 'g');
    nodesGroup.setAttribute('class', 'graph-nodes');
    for (const n of this.layout.nodes) {
      nodesGroup.appendChild(this.buildNode(n));
    }
    svg.appendChild(nodesGroup);

    container.innerHTML = '';
    container.appendChild(svg);

    this.wireSvgEvents();
    this.applyVisibility();
  }

  /** Tinta della sfera-autore = epoca (logica condivisa con author-sphere). */
  private nodeColor(n: GraphNode): string {
    return authorSphereColor(n.period ? [n.period] : undefined);
  }

  private buildNode(n: GraphNode): SVGGElement {
    const g = document.createElementNS(SVG_NS, 'g') as SVGGElement;
    g.setAttribute('class', 'graph-node');
    g.setAttribute('transform', `translate(${n.x}, ${n.y})`);
    g.dataset.nodeId = n.id;
    if (n.period) g.dataset.period = n.period;

    const color = this.nodeColor(n);
    const R = 24;
    const circle = document.createElementNS(SVG_NS, 'circle');
    circle.setAttribute('r', String(R));
    circle.setAttribute('class', 'node-circle');
    circle.style.fill = `url(#${sphereGradId(color)})`; // tinta per epoca (override CSS)
    g.appendChild(circle);

    // Riflesso glossy a due livelli + luce di rimbalzo → sfera 3D marcata
    const glossBig = document.createElementNS(SVG_NS, 'ellipse');
    glossBig.setAttribute('cx', String(-R * 0.30)); glossBig.setAttribute('cy', String(-R * 0.36));
    glossBig.setAttribute('rx', String(R * 0.48)); glossBig.setAttribute('ry', String(R * 0.32));
    glossBig.setAttribute('fill', 'rgba(255,255,255,0.50)');
    glossBig.setAttribute('pointer-events', 'none');
    g.appendChild(glossBig);
    const glossHot = document.createElementNS(SVG_NS, 'ellipse');
    glossHot.setAttribute('cx', String(-R * 0.34)); glossHot.setAttribute('cy', String(-R * 0.42));
    glossHot.setAttribute('rx', String(R * 0.18)); glossHot.setAttribute('ry', String(R * 0.12));
    glossHot.setAttribute('fill', 'rgba(255,255,255,0.92)');
    glossHot.setAttribute('pointer-events', 'none');
    g.appendChild(glossHot);
    const rim = document.createElementNS(SVG_NS, 'path');
    rim.setAttribute('d', `M ${-R * 0.66} ${R * 0.48} A ${R * 0.82} ${R * 0.82} 0 0 0 ${R * 0.66} ${R * 0.48}`);
    rim.setAttribute('fill', 'none');
    rim.setAttribute('stroke', sphereRim(color));
    rim.setAttribute('stroke-opacity', '0.66');
    rim.setAttribute('stroke-width', String(R * 0.13));
    rim.setAttribute('stroke-linecap', 'round');
    rim.setAttribute('pointer-events', 'none');
    g.appendChild(rim);

    // Initials as inner text
    const initials = document.createElementNS(SVG_NS, 'text');
    initials.setAttribute('y', '5');
    initials.setAttribute('text-anchor', 'middle');
    initials.setAttribute('class', 'node-initials');
    initials.textContent = computeInitials(n.name);
    g.appendChild(initials);

    // Display name below
    const label = document.createElementNS(SVG_NS, 'text');
    label.setAttribute('class', 'node-label');
    label.setAttribute('y', '42');
    label.setAttribute('text-anchor', 'middle');
    label.textContent = n.displayName;
    g.appendChild(label);

    // Year subtitle
    const yearText = document.createElementNS(SVG_NS, 'text');
    yearText.setAttribute('class', 'node-year');
    yearText.setAttribute('y', '56');
    yearText.setAttribute('text-anchor', 'middle');
    yearText.textContent = String(n.year);
    g.appendChild(yearText);

    return g;
  }

  private wireSvgEvents(): void {
    if (!this.svgEl) return;
    const svg = this.svgEl;
    svg.style.touchAction = 'none';

    this.addEventListener(svg, 'click', (ev) => {
      if (this.panDidMove) { this.panDidMove = false; return; }
      const target = ev.target as Element;
      const nodeEl = target.closest<SVGGElement>('[data-node-id]');
      if (nodeEl?.dataset.nodeId) this.handleNodeTap(nodeEl.dataset.nodeId);
      else this.handleBackgroundTap();
    });

    this.addEventListener(svg, 'mouseover', (ev) => {
      if (this.selectedId) return;
      const nodeEl = (ev.target as Element).closest<SVGGElement>('[data-node-id]');
      if (!nodeEl?.dataset.nodeId) return;
      this.applyHoverHighlight(nodeEl.dataset.nodeId);
    });
    this.addEventListener(svg, 'mouseout', (ev) => {
      if (this.selectedId) return;
      const mouseEv = ev as MouseEvent;
      const from = (mouseEv.target as Element).closest<SVGGElement>('[data-node-id]');
      const to = (mouseEv.relatedTarget as Element | null)?.closest<SVGGElement>('[data-node-id]');
      if (from && from !== to) this.applyHoverHighlight(null);
    });

    // Pan
    this.addEventListener(svg, 'mousedown', (ev) => this.panStart(ev as MouseEvent));
    this.addEventListener(svg, 'mousemove', (ev) => this.panMove(ev as MouseEvent));
    this.addEventListener(svg, 'mouseup', () => this.panEnd());
    this.addEventListener(svg, 'mouseleave', () => this.panEnd());

    // Wheel zoom
    this.addEventListener(svg, 'wheel', (ev) => {
      ev.preventDefault();
      const we = ev as WheelEvent;
      this.zoomAt(we.clientX, we.clientY, we.deltaY > 0 ? 1 + ZOOM_FACTOR : 1 - ZOOM_FACTOR);
    }, { passive: false } as AddEventListenerOptions);

    // Touch
    this.addEventListener(svg, 'touchstart', (ev) => {
      const te = ev as TouchEvent;
      if (te.touches.length === 2) this.pinchStart(te);
      else if (te.touches.length === 1) this.panStart(te.touches[0]);
    });
    this.addEventListener(svg, 'touchmove', (ev) => {
      const te = ev as TouchEvent;
      if (te.touches.length === 2) this.pinchMove(te);
      else if (te.touches.length === 1 && this.pinchStartDist === 0) this.panMove(te.touches[0]);
    });
    this.addEventListener(svg, 'touchend', () => { this.panEnd(); this.pinchStartDist = 0; });
    this.addEventListener(svg, 'touchcancel', () => { this.panEnd(); this.pinchStartDist = 0; });
  }

  private panStart(ev: MouseEvent | Touch): void {
    this.isPanning = true;
    this.panDidMove = false;
    this.panStartScreen = { x: ev.clientX, y: ev.clientY };
    this.panStartViewBox = { x: this.currentViewBox.x, y: this.currentViewBox.y };
  }

  private panMove(ev: MouseEvent | Touch): void {
    if (!this.isPanning || !this.svgEl) return;
    const dx = ev.clientX - this.panStartScreen.x;
    const dy = ev.clientY - this.panStartScreen.y;
    if (!this.panDidMove && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
    this.panDidMove = true;
    const scaleX = this.currentViewBox.w / this.svgEl.clientWidth;
    const scaleY = this.currentViewBox.h / this.svgEl.clientHeight;
    this.applyViewBox({
      x: this.panStartViewBox.x - dx * scaleX,
      y: this.panStartViewBox.y - dy * scaleY,
      w: this.currentViewBox.w,
      h: this.currentViewBox.h,
    });
  }

  private panEnd(): void { this.isPanning = false; }

  private zoomAt(clientX: number, clientY: number, factor: number): void {
    if (!this.svgEl) return;
    const vb = this.currentViewBox;
    let newW = vb.w * factor;
    let newH = vb.h * factor;
    const minW = this.baseViewBox.w * MIN_ZOOM;
    const maxW = this.baseViewBox.w * MAX_ZOOM;
    if (newW < minW) { newW = minW; newH = this.baseViewBox.h * MIN_ZOOM; }
    if (newW > maxW) { newW = maxW; newH = this.baseViewBox.h * MAX_ZOOM; }
    const rect = this.svgEl.getBoundingClientRect();
    const fx = (clientX - rect.left) / rect.width;
    const fy = (clientY - rect.top) / rect.height;
    this.applyViewBox({
      x: vb.x + (vb.w - newW) * fx,
      y: vb.y + (vb.h - newH) * fy,
      w: newW, h: newH,
    });
  }

  private pinchStart(ev: TouchEvent): void {
    const [a, b] = [ev.touches[0], ev.touches[1]];
    this.pinchStartDist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
    this.pinchStartViewBox = { ...this.currentViewBox };
    this.panDidMove = true;
  }

  private pinchMove(ev: TouchEvent): void {
    if (this.pinchStartDist === 0 || !this.svgEl) return;
    const [a, b] = [ev.touches[0], ev.touches[1]];
    const dist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
    const scale = this.pinchStartDist / dist;
    const midX = (a.clientX + b.clientX) / 2;
    const midY = (a.clientY + b.clientY) / 2;
    let newW = this.pinchStartViewBox.w * scale;
    let newH = this.pinchStartViewBox.h * scale;
    const minW = this.baseViewBox.w * MIN_ZOOM;
    const maxW = this.baseViewBox.w * MAX_ZOOM;
    if (newW < minW) { newW = minW; newH = this.baseViewBox.h * MIN_ZOOM; }
    if (newW > maxW) { newW = maxW; newH = this.baseViewBox.h * MAX_ZOOM; }
    const rect = this.svgEl.getBoundingClientRect();
    const fx = (midX - rect.left) / rect.width;
    const fy = (midY - rect.top) / rect.height;
    this.applyViewBox({
      x: this.pinchStartViewBox.x + (this.pinchStartViewBox.w - newW) * fx,
      y: this.pinchStartViewBox.y + (this.pinchStartViewBox.h - newH) * fy,
      w: newW, h: newH,
    });
  }

  private handleNodeTap(id: string): void {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const isSecondTapOnSame = this.lastTapId === id && now - this.lastTapTime < DOUBLE_TAP_WINDOW_MS;
    if (isSecondTapOnSame) {
      this.lastTapId = null;
      this.lastTapTime = 0;
      this.context.bus.emit('navigate:author', { id });
      return;
    }
    this.lastTapId = id;
    this.lastTapTime = now;
    this.selectNode(id);
  }

  private handleBackgroundTap(): void {
    this.lastTapId = null;
    this.lastTapTime = 0;
    if (this.selectedId) this.deselectNode();
  }

  private selectNode(id: string): void {
    if (!this.layout) return;
    this.selectedId = id;
    this.connectedToSelected = new Set([id]);
    for (const e of this.layout.edges) {
      if (e.sourceId === id) this.connectedToSelected.add(e.targetId);
      else if (e.targetId === id) this.connectedToSelected.add(e.sourceId);
    }
    // NIENTE zoom/spostamento sulla selezione: la timeline resta STABILE,
    // si evidenziano solo le connessioni (richiesta utente).
    this.applyVisibility();
  }

  private deselectNode(): void {
    this.selectedId = null;
    this.connectedToSelected.clear();
    this.applyViewBox(this.baseViewBox);
    this.applyVisibility();
  }

  private applyViewBox(vb: ViewBox): void {
    if (!this.svgEl) return;
    // Clamp: niente pan "a oltranza" oltre il contenuto, niente su/giù.
    // - x limitato a [0, baseW - w] → si scorre solo dove c'è contenuto
    //   (a zoom base w === baseW ⇒ x bloccato a 0: nessuno spostamento inutile);
    // - y sempre centrato verticalmente (la timeline è orizzontale).
    const b = this.baseViewBox;
    const w = vb.w, h = vb.h;
    const x = Math.min(Math.max(vb.x, b.x), b.x + Math.max(0, b.w - w));
    const y = b.y + (b.h - h) / 2;
    this.currentViewBox = { x, y, w, h };
    this.svgEl.setAttribute('viewBox', `${x} ${y} ${w} ${h}`);
  }

  private applyHoverHighlight(id: string | null): void {
    if (!this.svgEl) return;
    const nodes = this.svgEl.querySelectorAll<SVGGElement>('.graph-node');
    const edges = this.svgEl.querySelectorAll<SVGPathElement>('.graph-edge');
    if (id === null || !this.layout) {
      nodes.forEach((n) => { n.classList.remove('is-hovered'); n.classList.remove('is-connected'); });
      edges.forEach((e) => e.classList.remove('is-incident'));
      return;
    }
    const connected = new Set<string>([id]);
    edges.forEach((e) => {
      const isIncident = e.dataset.source === id || e.dataset.target === id;
      e.classList.toggle('is-incident', isIncident);
      if (isIncident) {
        connected.add(e.dataset.source!);
        connected.add(e.dataset.target!);
      }
    });
    nodes.forEach((n) => {
      const nid = n.dataset.nodeId!;
      n.classList.toggle('is-hovered', nid === id);
      n.classList.toggle('is-connected', connected.has(nid) && nid !== id);
    });
  }

  private applyVisibility(): void {
    if (!this.layout || !this.svgEl) return;
    const nodes = this.svgEl.querySelectorAll<SVGGElement>('.graph-node');
    const edges = this.svgEl.querySelectorAll<SVGPathElement>('.graph-edge');
    nodes.forEach((n) => n.classList.remove('is-hovered'));

    if (this.selectedId) {
      const sel = this.selectedId;
      const connected = this.connectedToSelected;
      nodes.forEach((n) => {
        const nid = n.dataset.nodeId!;
        const isSelected = nid === sel;
        const isConnected = connected.has(nid) && !isSelected;
        n.classList.toggle('is-selected', isSelected);
        n.classList.toggle('is-connected', isConnected);
        n.classList.toggle('is-dimmed', !connected.has(nid));
      });
      edges.forEach((e) => {
        const isIncident = e.dataset.source === sel || e.dataset.target === sel;
        e.classList.toggle('is-incident', isIncident);
        e.classList.toggle('is-dimmed', !isIncident);
      });
      return;
    }

    const matchIds = new Set(this.filterNodes().map((n) => n.id));
    const hasFilter = this.hasAnyFilter();
    nodes.forEach((n) => {
      n.classList.remove('is-selected');
      n.classList.remove('is-connected');
      n.classList.toggle('is-dimmed', hasFilter && !matchIds.has(n.dataset.nodeId!));
    });
    edges.forEach((e) => {
      e.classList.remove('is-incident');
      const dim = hasFilter && (!matchIds.has(e.dataset.source!) || !matchIds.has(e.dataset.target!));
      e.classList.toggle('is-dimmed', dim);
    });
  }

  private hasAnyFilter(): boolean {
    const f = this.filters;
    return Boolean((f.periods?.length) || (f.movements?.length) || (f.genres?.length));
  }

  private filterNodes(): GraphNode[] {
    if (!this.layout) return [];
    const { periods, movements, genres } = this.filters;
    const authorById = new Map(this.allAuthors.map((a) => [a.id, a]));
    return this.layout.nodes.filter((n) => {
      const a = authorById.get(n.id);
      if (!a) return false;
      if (periods?.length && !periods.some((p) => a.periods.includes(p))) return false;
      if (movements?.length && !movements.some((m) => a.movements.includes(m))) return false;
      if (genres?.length && !genres.some((g) => a.genres.includes(g))) return false;
      return true;
    });
  }

  private t(key: string): string {
    return this.context.i18n.t(key);
  }
}

// Mark RelationKind as used (re-export to suppress unused-import warning if any)
export type _RelationKind = RelationKind;
